"use client";

import { Box, VStack, Text } from "@chakra-ui/react";
import { useEffect, useId, useState } from "react";
import { useTranslations } from "next-intl";
import { useBenchmarkContext } from "../_context/BenchmarkContext";
import { KnootLottie } from "@/_components/ui/lotties/Lottie";
import { SelfAdsPill } from "@/_components/ui/SelfAds2Option";

const STEP_KEYS = [
  "parsing",
  "extracting",
  "evaluatingAts",
  "runningAi",
  "generatingPlan",
  "finalising",
] as const;

export type AnalysisLoadingViewProps = {
  progress: number;
  /** Ring diameter in px. Default 500 (full), Simple uses ~240. */
  ringSize?: number;
  ringStroke?: number;
  showLottie?: boolean;
  /** Override full-viewport layout (useful in Storybook). */
  minH?: string | number;
  progressFontSize?: string;
};

export function AnalysisLoadingView({
  progress,
  ringSize = 500,
  ringStroke = 10,
  showLottie = true,
  minH = "100vh",
  progressFontSize = "3xl",
}: AnalysisLoadingViewProps) {
  const t = useTranslations("resume_benchmark");
  const [stepIndex, setStepIndex] = useState(0);
  const gradientId = useId().replace(/:/g, "");

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex((i) => (i + 1) % STEP_KEYS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const ringRadius = (ringSize - ringStroke) / 2 - 4;
  const ringCenter = ringSize / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const dashOffset =
    ringCircumference -
    (Math.min(Math.max(progress, 0), 100) / 100) * ringCircumference;

  const titleSize = showLottie ? "xl" : "lg";
  const percentSize = showLottie ? "3xl" : progressFontSize;
  const contentGap = showLottie ? 5 : 3;
  const contentPx = showLottie ? 10 : 6;

  return (
    <Box
      minH={minH}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <VStack gap={8} maxW="480px" w="full" textAlign="center">
        <Box
          position="relative"
          w={`${ringSize}px`}
          h={`${ringSize}px`}
          mx="auto"
        >
          <Box
            as="svg"
            position="absolute"
            inset={0}
            w="full"
            h="full"
            style={{ transform: "rotate(-90deg)" }}
            pointerEvents="none"
            zIndex={1}
          >
            <circle
              cx={ringCenter}
              cy={ringCenter}
              r={ringRadius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={ringStroke}
            />
            <circle
              cx={ringCenter}
              cy={ringCenter}
              r={ringRadius}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={ringStroke}
              strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 0.75s ease-out" }}
            />
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#5B21F4" />
                <stop offset="100%" stopColor="#4C1DD1" />
              </linearGradient>
            </defs>
          </Box>

          <VStack
            position="absolute"
            inset={0}
            justify="center"
            gap={contentGap}
            px={contentPx}
            zIndex={0}
          >
            {showLottie ? (
              <Box position="relative" w={60} h={60}>
                <KnootLottie
                  animationName="fileLoading"
                  height="100%"
                  width="100%"
                />
              </Box>
            ) : null}

            <VStack gap={2}>
              <Text fontSize={titleSize} fontWeight="bold" color="white">
                {t("loading.title")}
              </Text>
              <Text
                fontSize="sm"
                color="neutral.500"
                minH="20px"
                transition="all 0.3s"
                lineClamp={2}
                w="full"
              >
                {t(`loading.steps.${STEP_KEYS[stepIndex]}`)}
              </Text>
              <Text
                fontSize={percentSize}
                fontWeight="light"
                color="primary.500"
                mt={1}
                fontFamily="mono"
                textStyle={progressFontSize}
              >
                {t("loading.progress", { progress: Math.round(progress) })}
              </Text>
            </VStack>
          </VStack>
        </Box>

        <Box w="full">
          <SelfAdsPill href="/" />
        </Box>
        <Text fontSize="xs" color="neutral.600">
          {t("loading.disclaimer")}
        </Text>
      </VStack>
    </Box>
  );
}

/** Full loading screen — uses BenchmarkContext progress. */
export const AnalysisLoading = () => {
  const { progress } = useBenchmarkContext();
  return (
    <AnalysisLoadingView
      progress={progress}
      ringSize={340}
      ringStroke={10}
      showLottie={false}
      progressFontSize="5xl"
    />
  );
};

export type AnalysisLoadingSimpleProps = {
  /** Progress 0–100. Required so it works outside BenchmarkProvider (e.g. Storybook). */
  progress: number;
  minH?: string | number;
};

/** Compact loading ring — smaller circle, no Lottie, same copy & ads. */
export const AnalysisLoadingSimple = ({
  progress,
  minH = "100vh",
}: AnalysisLoadingSimpleProps) => {
  return (
    <AnalysisLoadingView
      progress={progress}
      ringSize={340}
      ringStroke={8}
      showLottie={false}
      minH={minH}
      progressFontSize="2xl"
    />
  );
};
